/**
 * MessagesController — /api/v1/messages
 * Mirrors: src/Aidepo.Api/Controllers/MessagesController.cs
 *
 * Chat-style endpoint. Text or multipart audio in; eventId out for polling
 * via voice.getVoiceResult().
 */
import { apiGet, apiPost, apiPostForm } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';
import type { AgentActionWire, ClarifyOptionWire } from './voice.js';

/** Message role on the server: user input or assistant reply. */
export const MessageRole = {
  User: 'user',
  Assistant: 'assistant',
} as const;
export type MessageRole = (typeof MessageRole)[keyof typeof MessageRole];

/** How the user authored the message. */
export const InputKind = {
  Voice: 'voice',
  Text: 'text',
} as const;
export type InputKind = (typeof InputKind)[keyof typeof InputKind];

/** Lifecycle status of a VoiceEvent row. */
export const MessageStatus = {
  Pending: 'pending',
  Processing: 'processing',
  Applied: 'applied',
  Edited: 'edited',
  Cancelled: 'cancelled',
} as const;
export type MessageStatus = (typeof MessageStatus)[keyof typeof MessageStatus];

export interface MessageRow {
  readonly id: Uuid;
  readonly role: string;
  readonly inputKind: string;
  readonly content: string;
  readonly status: string;
  readonly createdAt: IsoDateTime;
  readonly whisperLatencyMs: number;
  readonly agentLatencyMs: number;
  /**
   * ChangeRecord, доступный для отката из чата (hh-2dl). Присутствует только
   * у assistant-сообщений успешных команд и пока окно отката открыто (24h).
   */
  readonly undoChangeId?: Uuid | null;
  /** Структурные действия assistant-сообщения (hh-4fc). */
  readonly actions?: readonly AgentActionWire[];
  /** Варианты уточнения assistant-сообщения (hh-5nh). */
  readonly clarifyOptions?: readonly ClarifyOptionWire[];
}

export interface ListMessagesResponse {
  readonly messages: readonly MessageRow[];
}

export interface SendTextRequest {
  readonly text: string;
  /**
   * Источник текста (aidepo-64). 'text' — ручной ввод с клавиатуры (по
   * умолчанию); 'voice_native' — речь, распознанная на устройстве нативно
   * (Web Speech API). Сервер пишет это в VoiceEvent.InputKind для метрики
   * «доля native vs whisper».
   */
  readonly source?: 'text' | 'voice_native';
}

export interface SubmitResponse {
  readonly eventId: Uuid;
  readonly jobId: Uuid;
  readonly content: string;
  readonly whisperLatencyMs: number;
}

/** GET /api/v1/messages?limit=N — chat history (user + assistant interleaved). */
export function listMessages(limit = 50): Promise<ListMessagesResponse> {
  const qs = new URLSearchParams({ limit: String(limit) }).toString();
  return apiGet<ListMessagesResponse>(`/api/v1/messages?${qs}`);
}

/** POST /api/v1/messages/text — submit a text message; agent job kicks off. */
export function sendText(req: SendTextRequest): Promise<SubmitResponse> {
  return apiPost<SubmitResponse, SendTextRequest>('/api/v1/messages/text', req);
}

/** POST /api/v1/messages/voice — multipart audio upload; STT runs synchronously. */
export function sendVoice(audio: Blob, filename = 'command.webm'): Promise<SubmitResponse> {
  const form = new FormData();
  form.append('audio', audio, filename);
  return apiPostForm<SubmitResponse>('/api/v1/messages/voice', form);
}
